/**
 * NutritionSettingsModal - Simplified settings for mobile app
 * 
 * Features:
 * 1. "Onboarding neu starten" - Start step-by-step wizard
 * 2. "Manuelle Eingabe" - For users who know their targets
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { X, Sparkles, Calculator, Zap, Scale, Utensils, Target, Save, RefreshCw, Loader2, ArrowLeft, Mail } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const NutritionSettingsModal = ({ open, onClose, token, onSettingsChanged, onRestartOnboarding }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('overview'); // 'overview' or 'manual'
  const [weeklySummaryEnabled, setWeeklySummaryEnabled] = useState(false);
  const [weeklySummaryLoading, setWeeklySummaryLoading] = useState(false);
  
  // Current goals
  const [currentGoals, setCurrentGoals] = useState({
    daily_calories: 2000,
    protein_grams: 150,
    carbs_grams: 250,
    fat_grams: 65
  });
  
  // Manual form
  const [manualForm, setManualForm] = useState({
    daily_calories: '',
    protein_grams: '',
    carbs_grams: '',
    fat_grams: ''
  });

  useEffect(() => {
    if (open) {
      if (token) {
        loadGoals();
        loadWeeklySummaryPref();
      } else {
        // No token - show defaults immediately
        setLoading(false);
      }
    }
  }, [open, token]);

  const loadGoals = async () => {
    setLoading(true);
    try {
      // Get actual goals from the goals endpoint (where the real calories are stored)
      const response = await axios.get(`${API}/portal/nutrition/goals`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000 // 15 second timeout
      });
      const data = response.data;
      
      // Use actual stored goals
      const goals = {
        daily_calories: data.daily_calories || 2000,
        protein_grams: Math.round(data.daily_protein || 150),
        carbs_grams: Math.round(data.daily_carbs || 250),
        fat_grams: Math.round(data.daily_fat || 65)
      };
      
      setCurrentGoals(goals);
      setManualForm({
        daily_calories: goals.daily_calories.toString(),
        protein_grams: goals.protein_grams.toString(),
        carbs_grams: goals.carbs_grams.toString(),
        fat_grams: goals.fat_grams.toString()
      });
    } catch (error) {
      console.error('Error loading nutrition goals:', error.message);
      // Set defaults on error so UI still shows
      setCurrentGoals({
        daily_calories: 2000,
        protein_grams: 150,
        carbs_grams: 250,
        fat_grams: 65
      });
      setManualForm({
        daily_calories: '2000',
        protein_grams: '150',
        carbs_grams: '250',
        fat_grams: '65'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveManual = async () => {
    const calories = parseInt(manualForm.daily_calories);
    const protein = parseInt(manualForm.protein_grams);
    const carbs = parseInt(manualForm.carbs_grams);
    const fat = parseInt(manualForm.fat_grams);
    
    if (!calories || calories < 1000 || calories > 10000) {
      toast.error('Kalorien: 1000-10000 kcal');
      return;
    }
    if (!protein || protein < 30 || protein > 500) {
      toast.error('Protein: 30-500g');
      return;
    }
    if (!carbs || carbs < 0 || carbs > 1000) {
      toast.error('Kohlenhydrate: 0-1000g');
      return;
    }
    if (!fat || fat < 20 || fat > 300) {
      toast.error('Fett: 20-300g');
      return;
    }
    
    // Calculate percentages from user-entered grams
    const proteinPercent = Math.round((protein * 4 / calories) * 100);
    const carbsPercent = Math.round((carbs * 4 / calories) * 100);
    const fatPercent = Math.round((fat * 9 / calories) * 100);
    
    // Calculate actual grams based on calories and percentages (ensures 100% match)
    const finalProtein = Math.round((calories * proteinPercent / 100) / 4);
    const finalCarbs = Math.round((calories * carbsPercent / 100) / 4);
    const finalFat = Math.round((calories * fatPercent / 100) / 9);
    
    setSaving(true);
    try {
      await axios.put(`${API}/portal/nutrition/goals`, {
        daily_calories: calories,
        daily_protein: finalProtein,
        daily_carbs: finalCarbs,
        daily_fat: finalFat,
        protein_percent: proteinPercent,
        carbs_percent: carbsPercent,
        fat_percent: fatPercent,
        mode: 'manual',
        source: 'manual_input'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Ziele gespeichert!');
      setMode('overview');
      loadGoals();
      if (onSettingsChanged) onSettingsChanged();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const loadWeeklySummaryPref = async () => {
    try {
      const res = await axios.get(`${API}/portal/weekly-summary/settings`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 10000
      });
      setWeeklySummaryEnabled(res.data?.enabled || false);
    } catch { /* ignore */ }
  };

  const toggleWeeklySummary = async () => {
    setWeeklySummaryLoading(true);
    const newVal = !weeklySummaryEnabled;
    try {
      await axios.put(`${API}/portal/weekly-summary/settings`,
        { enabled: newVal },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }
      );
      setWeeklySummaryEnabled(newVal);
      toast.success(newVal ? 'Wöchentliche Zusammenfassung aktiviert' : 'Wöchentliche Zusammenfassung deaktiviert');
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setWeeklySummaryLoading(false);
    }
  };

  const handleRestartOnboarding = () => {
    sessionStorage.removeItem('nutrition_onboarding_skipped');
    onClose();
    if (onRestartOnboarding) {
      onRestartOnboarding();
    }
    toast.success('Onboarding wird gestartet...');
  };

  const calculateCaloriesFromMacros = () => {
    const protein = parseInt(manualForm.protein_grams) || 0;
    const carbs = parseInt(manualForm.carbs_grams) || 0;
    const fat = parseInt(manualForm.fat_grams) || 0;
    return (protein * 4) + (carbs * 4) + (fat * 9);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-zinc-900 w-full max-w-md max-h-[90vh] rounded-t-3xl sm:rounded-3xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-zinc-800 flex justify-between items-center">
          <h2 className="font-bold text-white text-lg">⚙️ Ernährungs-Einstellungen</h2>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-full">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-yellow-400" />
            </div>
          ) : mode === 'overview' ? (
            <div className="space-y-4">
              {/* Current Goals */}
              <div className="p-4 bg-zinc-800/50 rounded-xl">
                <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-yellow-400" />
                  Aktuelle Ziele
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-3 bg-zinc-800 rounded-lg text-center">
                    <p className="text-xs text-zinc-400">Kalorien</p>
                    <p className="text-lg font-bold text-yellow-400">{currentGoals.daily_calories}</p>
                  </div>
                  <div className="p-3 bg-zinc-800 rounded-lg text-center">
                    <p className="text-xs text-zinc-400">Protein</p>
                    <p className="text-lg font-bold text-red-400">{currentGoals.protein_grams}g</p>
                  </div>
                  <div className="p-3 bg-zinc-800 rounded-lg text-center">
                    <p className="text-xs text-zinc-400">Kohlenhydrate</p>
                    <p className="text-lg font-bold text-blue-400">{currentGoals.carbs_grams}g</p>
                  </div>
                  <div className="p-3 bg-zinc-800 rounded-lg text-center">
                    <p className="text-xs text-zinc-400">Fett</p>
                    <p className="text-lg font-bold text-orange-400">{currentGoals.fat_grams}g</p>
                  </div>
                </div>
              </div>

              {/* Option 1: Onboarding */}
              <div className="p-4 bg-gradient-to-br from-purple-500/20 to-violet-500/20 rounded-xl border border-purple-500/30">
                <div className="flex items-center gap-3 mb-2">
                  <Sparkles className="w-6 h-6 text-purple-400" />
                  <h3 className="font-semibold text-white">KI-gestützte Einrichtung</h3>
                </div>
                <p className="text-sm text-zinc-400 mb-3">
                  Optimale Ziele in 7 Schritten berechnen lassen
                </p>
                <button
                  onClick={handleRestartOnboarding}
                  className="w-full py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                  data-testid="restart-onboarding-mobile-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                  Onboarding starten
                </button>
              </div>

              {/* Option 2: Manual */}
              <div className="p-4 bg-gradient-to-br from-yellow-500/20 to-amber-500/20 rounded-xl border border-yellow-500/30">
                <div className="flex items-center gap-3 mb-2">
                  <Calculator className="w-6 h-6 text-yellow-400" />
                  <h3 className="font-semibold text-white">Manuelle Eingabe</h3>
                </div>
                <p className="text-sm text-zinc-400 mb-3">
                  Du kennst deine Ziele bereits?
                </p>
                <button
                  onClick={() => setMode('manual')}
                  className="w-full py-3 border border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20 rounded-xl font-medium flex items-center justify-center gap-2"
                  data-testid="manual-input-mobile-btn"
                >
                  <Utensils className="w-4 h-4" />
                  Eigene Werte eingeben
                </button>
              </div>

              {/* Weekly Summary Toggle */}
              <div className="p-4 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-xl border border-emerald-500/30">
                <div className="flex items-center gap-3 mb-2">
                  <Mail className="w-6 h-6 text-emerald-400" />
                  <h3 className="font-semibold text-white">Wochen-Zusammenfassung</h3>
                </div>
                <p className="text-sm text-zinc-400 mb-3">
                  Jeden Sonntag KI-Analyse per E-Mail
                </p>
                <button
                  onClick={toggleWeeklySummary}
                  disabled={weeklySummaryLoading}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
                    weeklySummaryEnabled
                      ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400'
                      : 'bg-zinc-800 border-zinc-700 text-zinc-400'
                  }`}
                  data-testid="mobile-weekly-summary-toggle"
                >
                  <span className="text-sm font-medium">
                    {weeklySummaryEnabled ? 'Aktiviert' : 'Nicht aktiviert'}
                  </span>
                  <div className={`w-10 h-6 rounded-full transition-all relative ${
                    weeklySummaryEnabled ? 'bg-emerald-500' : 'bg-zinc-600'
                  }`}>
                    <div className={`w-4 h-4 rounded-full bg-white absolute top-1 transition-all ${
                      weeklySummaryEnabled ? 'left-5' : 'left-1'
                    }`} />
                  </div>
                </button>
              </div>
            </div>
          ) : (
            /* Manual Input */
            <div className="space-y-4">
              <button
                onClick={() => setMode('overview')}
                className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Zurück
              </button>

              {/* Calories */}
              <div>
                <label className="text-zinc-300 text-sm flex items-center gap-2 mb-1">
                  <Zap className="w-4 h-4 text-yellow-400" />
                  Tagesziel (kcal)
                </label>
                <input
                  type="number"
                  value={manualForm.daily_calories}
                  onChange={(e) => setManualForm({ ...manualForm, daily_calories: e.target.value })}
                  className="w-full p-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white text-lg"
                  placeholder="z.B. 2500"
                  data-testid="manual-calories-mobile-input"
                />
              </div>

              {/* Protein */}
              <div>
                <label className="text-zinc-300 text-sm flex items-center gap-2 mb-1">
                  <Scale className="w-4 h-4 text-red-400" />
                  Protein (g)
                </label>
                <input
                  type="number"
                  value={manualForm.protein_grams}
                  onChange={(e) => setManualForm({ ...manualForm, protein_grams: e.target.value })}
                  className="w-full p-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white text-lg"
                  placeholder="z.B. 150"
                  data-testid="manual-protein-mobile-input"
                />
              </div>

              {/* Carbs */}
              <div>
                <label className="text-zinc-300 text-sm flex items-center gap-2 mb-1">
                  <Utensils className="w-4 h-4 text-blue-400" />
                  Kohlenhydrate (g)
                </label>
                <input
                  type="number"
                  value={manualForm.carbs_grams}
                  onChange={(e) => setManualForm({ ...manualForm, carbs_grams: e.target.value })}
                  className="w-full p-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white text-lg"
                  placeholder="z.B. 250"
                  data-testid="manual-carbs-mobile-input"
                />
              </div>

              {/* Fat */}
              <div>
                <label className="text-zinc-300 text-sm flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-orange-400" />
                  Fett (g)
                </label>
                <input
                  type="number"
                  value={manualForm.fat_grams}
                  onChange={(e) => setManualForm({ ...manualForm, fat_grams: e.target.value })}
                  className="w-full p-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white text-lg"
                  placeholder="z.B. 70"
                  data-testid="manual-fat-mobile-input"
                />
              </div>

              {/* Macro Check */}
              {manualForm.protein_grams && manualForm.carbs_grams && manualForm.fat_grams && (
                <div className="p-3 bg-zinc-800/50 rounded-lg">
                  <p className="text-xs text-zinc-400">Kalorien aus Makros:</p>
                  <p className="text-lg font-bold">
                    <span className={Math.abs(calculateCaloriesFromMacros() - parseInt(manualForm.daily_calories || 0)) < 100 
                      ? 'text-green-400' 
                      : 'text-orange-400'
                    }>
                      {calculateCaloriesFromMacros()} kcal
                    </span>
                  </p>
                </div>
              )}

              {/* Save */}
              <button
                onClick={handleSaveManual}
                disabled={saving}
                className="w-full py-4 bg-yellow-500 hover:bg-yellow-600 text-black rounded-xl font-bold flex items-center justify-center gap-2"
                data-testid="save-manual-mobile-btn"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                Speichern
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NutritionSettingsModal;
